import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FiTruck, FiPlus, FiRefreshCw } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TXN_LABEL = {
  delivery_charge: 'Delivery charge',
  cash_topup: 'Cash top-up',
  upi_topup: 'UPI top-up',
  admin_topup: 'Admin top-up',
  razorpay_topup: 'Online top-up',
  admin_adjustment: 'Adjustment',
};

export default function DeliveryBalancePanel({ userId, currentBalance, getAuthHeader, onChanged }) {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(currentBalance ?? 0);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setBalance(currentBalance ?? 0);
  }, [currentBalance]);

  const fetchTxns = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/users/${userId}/delivery-transactions`,
        { headers: getAuthHeader() }
      );
      setTxns(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId, getAuthHeader]);

  useEffect(() => {
    fetchTxns();
  }, [fetchTxns]);

  const handleAdd = async () => {
    const amt = parseFloat(amount);
    if (!amt || Number.isNaN(amt)) {
      setError('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/admin/users/${userId}/delivery-credit`,
        { amount: amt, payment_method: method, note: note.trim() || null },
        { headers: getAuthHeader() }
      );
      setBalance(res.data.new_balance);
      setAmount('');
      setNote('');
      fetchTxns();
      onChanged?.(res.data.new_balance);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add credit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="border rounded-xl p-4 bg-white"
      data-testid="delivery-balance-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <FiTruck className="text-purple-600" /> Delivery Balance
        </h3>
        <button
          onClick={fetchTxns}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          data-testid="refresh-delivery-txns"
        >
          <FiRefreshCw size={12} /> Refresh
        </button>
      </div>

      <div
        className={`rounded-lg px-4 py-3 mb-4 ${
          balance < 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
        }`}
      >
        <div className="text-xs text-gray-600">Current balance</div>
        <div
          className={`text-2xl font-bold ${balance < 0 ? 'text-red-700' : 'text-green-700'}`}
          data-testid="user-delivery-balance"
        >
          ₹{Number(balance).toFixed(2)}
        </div>
        {balance < 0 && (
          <div className="text-xs text-red-700 mt-1">
            Member owes ₹{Math.abs(balance).toFixed(2)} — blocked from new holds.
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="font-semibold text-sm mb-2 flex items-center gap-1">
          <FiPlus /> Record cash / UPI top-up
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input
            type="number"
            placeholder="Amount (₹)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
            data-testid="delivery-credit-amount"
          />
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
            data-testid="delivery-credit-method"
          >
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
            <option value="other">Other</option>
          </select>
        </div>
        <input
          type="text"
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="border rounded px-2 py-1 text-sm w-full mb-2"
          data-testid="delivery-credit-note"
        />
        {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
        <button
          onClick={handleAdd}
          disabled={submitting}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          data-testid="delivery-credit-submit"
        >
          {submitting ? 'Saving…' : 'Add to balance'}
        </button>
        <p className="text-[11px] text-gray-500 mt-1">
          Enter a negative amount to make a corrective debit.
        </p>
      </div>

      <div>
        <div className="text-sm font-semibold text-gray-800 mb-2">Recent transactions</div>
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : txns.length === 0 ? (
          <div className="text-sm text-gray-500">No transactions yet.</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {txns.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between text-xs border-b last:border-b-0 py-1.5"
                data-testid={`txn-${t.id}`}
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-800">
                    {TXN_LABEL[t.type] || t.type}
                  </div>
                  <div className="text-gray-500">
                    {new Date(t.created_at).toLocaleString()}
                    {t.notes ? ` • ${t.notes}` : ''}
                  </div>
                </div>
                <div
                  className={`font-semibold ${
                    t.delta < 0 ? 'text-red-600' : 'text-green-700'
                  }`}
                >
                  {t.delta < 0 ? '-' : '+'}₹{Math.abs(t.delta).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
